import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BarChartIcon from "@mui/icons-material/BarChart";
import LogoutIcon from "@mui/icons-material/Logout";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RuleIcon from "@mui/icons-material/Rule";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { signOut } from "aws-amplify/auth";
import { lazy, Suspense } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router";

/**
 * Ansichten getrennt nachladen.
 *
 * In einem Bündel wären es rund 1,5 MB — DataGrid und die Diagrammbibliothek
 * machen den Großteil aus, und wer nur zuordnet, braucht keines von beiden.
 * Die Übersicht als Einstieg zieht die Diagrammbibliothek nach, das DataGrid
 * aber weiterhin nicht.
 */
const Zuordnen = lazy(() =>
  import("./views/Zuordnen").then((m) => ({ default: m.Zuordnen })),
);
const Buchungen = lazy(() =>
  import("./views/Buchungen").then((m) => ({ default: m.Buchungen })),
);
const Konten = lazy(() =>
  import("./views/Konten").then((m) => ({ default: m.Konten })),
);
const Uebersicht = lazy(() =>
  import("./views/Uebersicht").then((m) => ({ default: m.Uebersicht })),
);
const Import = lazy(() =>
  import("./views/Import").then((m) => ({ default: m.Import })),
);

const NAVIGATION = [
  { pfad: "/uebersicht", text: "Übersicht", icon: <BarChartIcon /> },
  { pfad: "/buchungen", text: "Buchungen", icon: <ReceiptLongIcon /> },
  { pfad: "/konten", text: "Konten", icon: <AccountBalanceIcon /> },
  { pfad: "/zuordnen", text: "Zuordnen", icon: <RuleIcon /> },
  { pfad: "/import", text: "Import", icon: <UploadFileIcon /> },
];

export function App({ onAbgemeldet }: { onAbgemeldet: () => void }) {
  const { pathname } = useLocation();
  const aktiv = NAVIGATION.find((n) => pathname.startsWith(n.pfad))?.pfad;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            HouseholdBook
          </Typography>
          <Tooltip title="Abmelden">
            <IconButton
              color="inherit"
              aria-label="Abmelden"
              onClick={async () => {
                await signOut();
                onAbgemeldet();
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        <Tabs
          value={aktiv ?? false}
          textColor="inherit"
          indicatorColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {NAVIGATION.map((eintrag) => (
            <Tab
              key={eintrag.pfad}
              value={eintrag.pfad}
              label={eintrag.text}
              icon={eintrag.icon}
              iconPosition="start"
              component={NavLink}
              to={eintrag.pfad}
              sx={{ minHeight: 56 }}
            />
          ))}
        </Tabs>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3, flex: 1, display: "flex" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Suspense fallback={<LinearProgress />}>
            <Routes>
              {/* Die Übersicht ist der Einstieg: erst der Stand, dann die Arbeit. */}
              <Route path="/" element={<Navigate to="/uebersicht" replace />} />
              <Route path="/zuordnen" element={<Zuordnen />} />
              <Route path="/buchungen" element={<Buchungen />} />
              <Route path="/konten" element={<Konten />} />
              <Route path="/uebersicht" element={<Uebersicht />} />
              <Route path="/import" element={<Import />} />
              <Route path="*" element={<Navigate to="/uebersicht" replace />} />
            </Routes>
          </Suspense>
        </Box>
      </Container>
    </Box>
  );
}
